import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  MenuItem,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  DialogActions,
  Menu,
  Tooltip,
  Skeleton,
  Divider,
  Paper,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  TableChart as TableChartIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import { useTheme, keyframes } from "@emotion/react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { format } from "date-fns";
import AnchorTemporaryDrawer from "../../components/Drawer";

import AuditTrailDialog from "../../components/AuditTrail/AuditTrailDialog";
import { logAuditChange } from "../../components/AuditTrail/auditLogger";
import {
  fetchDataEngine,
  fetchDataEngineCustomized,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../Axios/DynamicService";
import { useNavigate } from "react-router-dom";
import usePermissions from "../RBAC/PermissionsHook";
import useSubRightPermissionsHook from "../RBAC/SubRightPermissionHook";
import Swal from "sweetalert2";
import {
  executeAnyMutation,
  fetchJsonDynamic,
  generateColumnsFromResults,
} from "../DynamicServiceNew/jsondynamicservice";
import { Circles } from "react-loader-spinner";
import { LoanApprovalDialog } from "../../scenes/LoanManagement/LoanApprovalComponent";
import { GroupLoanApprovalDialog } from "../../scenes/GroupLoanManagement/LoanApprovalComponent";
import { AssetLoanApprovalDialog } from "../../scenes/AsssetLoanManagement/Approvals/LoanApprovalDialog";
import { DeviceLoanApprovalDialog } from "../../scenes/DeviceLoanManagement/Approvals/LoanApprovalDialog";
import {
  customerservice,
  HRService,
  loanManagementUrl,
  AssetFinanceUrl,
  AccountsandFinanceurl,
} from "../../config";
import { verifyGuarantor, ViewStatatementDetails } from "./DynamicFunctions";
import { LoanLimitDialog } from "../../scenes/CustomerLoanLimit/SetLoanLimitModel";
import { CustomerEmployerMappingDialog } from "../../scenes/Customers/customers/CustomerAwaitingApproval/MaptoEmployer";
import { CollateralValuationDialog } from "../../scenes/Customers/customers/ManageCustomer/CollateralAsset/ValuationModal";
import { generateColumnsFromQuery } from "../Axios/DynamicService";
import CustomerStatementDetails from "../../scenes/Customers/customers/ManageCustomer/CustomerStatement";
import { debounce } from "lodash";
import axios from "axios";
import { LeaveApplicationApprovalDialog } from "../../scenes/HRManagemenet/LeaveApprovals/Approvedeclinemodal";
import { StaffClaimApprovalDialog } from "../../scenes/HRManagemenet/StaffClaim/Approvedeclineclaim";
import { ImprestApprovalDialog } from "../../scenes/HRManagemenet/ImprestApplications/ApprovedeclineImprest";
import { useTour } from "../../context/TourContext";
import Joyride, { STATUS } from "react-joyride";

// ─── Animation keyframes ───────────────────────────────────────────────────────
const spinAnim = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const fadeInUp = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const popIn = keyframes`0% { transform: scale(0.7); opacity: 0.4; } 60% { transform: scale(1.18); } 100% { transform: scale(1); opacity: 1; }`;
const chipPulse = keyframes`0%,100% { transform: scale(1); } 50% { transform: scale(1.1); }`;

const escapeHtmlForSwal = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const sanitizeErrorMessage = (msg) => {
  if (!msg) return "Kindly contact Admin for more clarifications.";
  const str = String(msg);
  if (
    str.includes("Npgsql") ||
    str.includes("PostgresException") ||
    str.includes("SqlState") ||
    str.includes("does not exist") ||
    str.includes("function ") ||
    str.includes("column ") ||
    str.includes("relation ") ||
    str.includes("Database Schema Error") ||
    str.includes("at System.") ||
    str.includes("at Npgsql.") ||
    str.includes("Exception data:") ||
    str.includes("POSITION:") ||
    str.includes("Routine:")
  ) {
    console.error("[Backend Technical Error]:", str);
    return "Kindly contact Admin for more clarifications.";
  }
  return str;
};

const parseMutationErrorMessages = (rawError) => {
  try {
    const parsed =
      typeof rawError === "string" ? JSON.parse(rawError) : rawError;
    if (Array.isArray(parsed)) return parsed.map(sanitizeErrorMessage);
    if (typeof parsed === "string") return [sanitizeErrorMessage(parsed)];
    if (parsed?.Error) {
      console.error("[Backend System Error]:", parsed.Error);
      return [sanitizeErrorMessage(parsed.Error)];
    }
    if (parsed?.error) return [sanitizeErrorMessage(parsed.error)];
    return [sanitizeErrorMessage(JSON.stringify(parsed))];
  } catch {
    return [sanitizeErrorMessage(rawError)];
  }
};

const fireMutationError = (rawError, title = "Error!") => {
  const messages = parseMutationErrorMessages(rawError);
  if (messages.length > 1) {
    return Swal.fire({
      icon: "error",
      title,
      html: `<ul style="text-align:left; margin:0; padding-left:1.2em;">${messages
        .map((message) => `<li>${escapeHtmlForSwal(message)}</li>`)
        .join("")}</ul>`,
    });
  }
  return Swal.fire({
    icon: "error",
    title,
    text: messages[0] || "An unknown error occurred.",
  });
};

const DynamicTableForJson = ({
  title,
  subtitle,
  FormComponent,
  columns,
  query,
  base_url,
  actions,
  ignoreFields,
  setSelectedProductId,
  filterData,
  tourSteps = [],
  onDataChanged,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [error, setError] = useState(null);
  const { hasPermission } = usePermissions();
  const { hasSubRightPermission } = useSubRightPermissionsHook();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tableRowData, setRowData] = useState("");
  const [data, setData] = useState([]);
  const [usedData, setUsedData] = useState([]);
  const [searchData, setSearchedData] = useState([]);
  const [dataArray, setDataArray] = useState([]);
  const [refreshTable, setRefreshTable] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const RoleGroupId = parseInt(decodedToken.RoleGroupId);
  const CompanyId = parseInt(decodedToken.CompanyId);
  const UserId = parseInt(decodedToken.Id);
  const [openLoanModal, setOpenLoanModal] = useState(false);
  const [openAssetLoanModal, setOpenAssetLoanModal] = useState(false);
  const [openDeviceLoanModal, setOpenDeviceLoanModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [generatedColumn, setGeneratedColumn] = useState([]);
  const [openGroupLoanModal, setOpenGroupLoanModal] = useState(false);
  const [openLoanLimitModel, setOpenLoanLimitModel] = useState(false);
  const [openValuationModal, setOpenValuationModal] = useState(false);
  const [openMappingToEmployer, setOpenMappingToEmployer] = useState(false);
  const [openLeaveModul, setOpenLeaveModul] = useState(false);
  const [openStaffClaim, setOpenStaffClaim] = useState(false);
  const [openImprestModal, setOpenImprestModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [selectedAuditRow, setSelectedAuditRow] = useState(null);
  const [search, setSearch] = useState(false);
  const iisMobile = useMediaQuery("(max-width: 768px)");
  const [filteringdData, setFilteringdData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Must be created unconditionally, before the `if (error)`/`if (!data)`
  // early returns below — otherwise those returns skip this hook on some
  // renders and React throws "Rendered fewer hooks than expected." The ref
  // indirection lets the debounce wrapper be created once here while still
  // always calling the latest handleSearch (defined further down, and
  // reassigned into the ref on every render past the early returns).
  const handleSearchRef = useRef(null);
  const debouncedSearch = useRef(
    debounce((input) => handleSearchRef.current?.(input), 300),
  ).current;

  // Row action menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeMenuRow, setActiveMenuRow] = useState(null);

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setActiveMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Tour integration
  const { isFirstTime, touredPages, markPageAsToured, tourPages } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    if (
      isFirstTime &&
      !touredPages.includes(currentPath) &&
      tourSteps.length > 0
    ) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath, tourSteps]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath);
  };

  // ─── Business logic handlers ───────────────────────────────────────────────

  const handleActivateDeactivate = async (isActive, id) => {
    try {
      const actionType =
        isActive === true || isActive === 1 ? "deactivate" : "activate";
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: `Do you want to ${actionType} ${actions.entity}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType} it!`,
        cancelButtonText: "No, keep it",
      });

      if (confirmation.isConfirmed) {
        let mutationData = null;
        if (actions.modelName) {
          mutationData = {
            modelName: actions.activateDeactivate.modelName,
            isActive: !isActive,
            moduleIds: [id],
          };
        } else {
          mutationData = {
            [actions.activateDeactivate.modelName]: [id],
            isActive: !isActive,
          };
        }

        const response = await generateAndExecuteMutation(
          actions.activateDeactivate.inputObjectName,
          mutationData,
          actions.activateDeactivate.inputSubObjectName,
          base_url,
        );

        if (response && Object.keys(response)[0]) {
          const wasActive = isActive === true || isActive === 1 || isActive === "True";
          logAuditChange({
            entityType: actions.entity || "Record",
            recordId: id,
            diff: [
              {
                field: "Is Active",
                before: wasActive ? "Active / True" : "Inactive / False",
                after: wasActive ? "Inactive / False" : "Active / True",
              },
              {
                field: "Status",
                before: wasActive ? "Operational" : "Deactivated",
                after: wasActive ? "Deactivated" : "Operational",
              },
            ],
            userId: UserId,
            actionType: wasActive ? "deactivated" : "active",
          });

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `${actions.entity} ${actionType}d successfully.`,
          });
          setRefreshTable(true);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleApproveDisaaproveCollateralValuation = async (
    Status,
    Id,
    asset,
  ) => {
    try {
      const actionType = Status === "SUBMITTED" ? "Approve" : "Disapprove";
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: `Do you want to ${actionType} ${actions.entity}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType} it!`,
        cancelButtonText: "No, keep it",
      });

      if (confirmation.isConfirmed) {
        let mutationData = null;
        if (actionType === "Approve") {
          mutationData = {
            status: "APPROVED",
            id: Id,
            assetId: asset,
          };
        } else {
          mutationData = {
            status: "REJECTED",
            Id: Id,
            assetId: asset,
          };
        }

        const response = await generateAndExecuteMutation(
          actions.AprrovedRejectValuation.inputObjectName,
          mutationData,
          actions.AprrovedRejectValuation.inputSubObjectName,
          base_url,
        );

        if (response && Object.keys(response)[0]) {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `${actions.entity} ${actionType}d successfully.`,
          });
          setRefreshTable(true);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleViewStatement = (row) => {
    const raw = localStorage.getItem("token")
    if (!raw) {
      alert("Your session has expired. Please log in again.")
      return
    }

    let sababishaToken
    try {
      sababishaToken = JSON.parse(raw)
    } catch {
      sababishaToken = raw
    }

    const url = new URL("https://finsights.sababisha.com/en/sso")
    url.searchParams.set("token", sababishaToken)
    url.searchParams.set("statementId", row.StatementId)
    window.open(url.toString(), "_blank", "noopener,noreferrer")
  }


  //   const handleViewStatement = async (row) => {
  //   try {
  //     const statementId = row.StatementId;
  //     const customerId = localStorage.getItem("CustomerId");
  //     const sababishaToken = localStorage.getItem("token");

  //     if (!sababishaToken) {
  //       alert("Your session has expired. Please log in again.");
  //       return;
  //     }

  //     // Build a hidden form and POST to Finsights SSO endpoint
  //     const form = document.createElement("form");
  //     form.method = "POST";
  //     form.action = "https://finsights.sababisha.com/en/sso-bridge";
  //     form.target = "_blank";

  //     const fields = {
  //       sababisha_token: sababishaToken,
  //       statement_id: statementId,
  //       customer_id: customerId,
  //       redirect_to: "/en/apps/generate-summary"
  //     };

  //     Object.entries(fields).forEach(([key, value]) => {
  //       const input = document.createElement("input");
  //       input.type = "hidden";
  //       input.name = key;
  //       input.value = value ?? "";
  //       form.appendChild(input);
  //     });

  //     document.body.appendChild(form);
  //     form.submit();
  //     document.body.removeChild(form);
  //   } catch (error) {
  //     alert(`Error opening report: ${error.message}`);
  //   }
  // };
  // const handleViewStatement = async (row) => {
  //   try {
  //     const statementId = row.StatementId;
  //     const CustomerId = localStorage.getItem("CustomerId");
  //     const customerId = parseInt(CustomerId);
  //     const newTab = window.open("", "_blank");
  //     newTab.document.write(
  //       `<html><head><title>Loading...</title><style>body{font-family:Arial,sans-serif;display:flex;justify-content:center;align-items:center;height:100vh;margin:0;background-color:#f0f0f0;}.loading{font-size:24px;color:#333;}</style></head><body><div class="loading">Loading, please wait...</div></body></html>`,
  //     );
  //     newTab.document.write(
  //       `<html><head><title>Loading...</title><style>body{font-family:Arial,sans-serif;display:flex;justify-content:center;align-items:center;height:100vh;margin:0;background-color:#f0f0f0;}.loading{font-size:24px;color:#333;}</style></head><body><div class="loading">Loading, please wait...</div></body></html>`,
  //     );
  //     newTab.document.close();
  //     const response = await ViewStatatementDetails(
  //       { statementId, customerId },
  //       customerservice.generate_report_uri,
  //       () => {},
  //     );
  //     if (response.ok) {
  //       const htmlContent = await response.text();
  //       newTab.document.open();
  //       newTab.document.write(htmlContent);
  //       newTab.document.close();
  //     } else {
  //       alert("Failed to load the report. Please try again.");
  //       newTab.close();
  //     }
  //   } catch (error) {
  //     alert(`Error: ${error.message}`);
  //   }
  // };

  const handleSababisha = async (loanId, entityId, flag) => {
    try {
      const params = { p_entityid: entityId, p_flag: flag, p_loanid: loanId };
      const action =
        flag === 0 ? actions.initiateSababisha : actions.approveSababisha;
      const actionType = action.button_name;

      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: `${actionType} ?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType}!`,
        cancelButtonText: "Cancel",
      });

      if (confirmation.isConfirmed) {
        const response = await executeAnyMutation(
          action.inputObjectName,
          params,
          action.modelName,
          "false",
          base_url,
        );
        const parsedResults = JSON.parse(response.results);
        if (parsedResults) {
          if (parsedResults === "Success") {
            Swal.fire({
              icon: "success",
              title: "Success!",
              text: `${actions.entity} ${flag === 0 ? "initiated" : "approved"} succesfully.`,
            });
          } else {
            Swal.fire({ icon: "error", title: "Error!", text: parsedResults });
          }
          setRefreshTable(true);
        } else {
          throw new Error("Unexpected response structure.");
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const removeLeaveApprover = async (row) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Do you  want to remove this Approver?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Remove!",
        cancelButtonText: "No, keep",
      });
      if (confirmation.isConfirmed) {
        const mutationData = {
          id: row.Id,
          userId: row.UserId,
          isActive: false,
        };
        const response = await generateAndExecuteMutation(
          "addRemoveLeaveApprover",
          mutationData,
          "approvers",
          HRService.uri,
        );
        if (Object.values(response)[0] != null) {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Approver Removed successfully.`,
          });
          setRefreshTable(true);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const removeImprestApprover = async (row) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Do you  want to remove this Approver?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Remove!",
        cancelButtonText: "No, keep",
      });
      if (confirmation.isConfirmed) {
        const mutationData = {
          id: row.Id,
          userId: row.UserId,
          isActive: false,
        };
        const response = await generateAndExecuteMutation(
          "addRemoveRequisitionApprover",
          mutationData,
          "approvers",
          HRService.uri,
        );
        if (Object.values(response)[0] != null) {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Approver Removed successfully.`,
          });
          setRefreshTable(true);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const removeReleiever = async (row) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Do you  want to remove this Reliever?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Remove!",
        cancelButtonText: "No, keep",
      });
      if (confirmation.isConfirmed) {
        const mutationData = { id: row.Id, userId: row.Id, isActive: false };
        const response = await generateAndExecuteMutation(
          "addRemoveReliever",
          mutationData,
          "relievers",
          HRService.uri,
        );
        if (Object.values(response)[0] != null) {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Reliever Removed successfully.`,
          });
          setRefreshTable(true);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const cancelExpenseEntries = async (row) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Do you  want to Cancel this Expense?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Cancel!",
        cancelButtonText: "No, keep",
      });
      if (confirmation.isConfirmed) {
        const mutationData = {
          expenseEntryId: row.Id,
          isCancelled: row.Cancelled,
        };
        const response = await generateAndExecuteMutation(
          "cancelApproveExpensesEntries",
          mutationData,
          HRService.uri,
        );
        if (Object.values(response)[0] != null) {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Expense cancelled successfully.`,
          });
          setRefreshTable(true);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const removeSurrenderLine = async (row) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: `Do you  want to remove this line?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Remove!",
        cancelButtonText: "No, keep",
      });
      if (confirmation.isConfirmed) {
        const mutationData = {
          id: row.Id,
          applicationId: row.ApplicationId,
          amount: row.Amount,
          description: row.Description,
          isActive: 0,
        };
        if (actions.dataObjectForMutation.isSurrenderLine) {
          mutationData.surrenderId = row.SurrenderId;
        }
        const response = await generateAndExecuteMutation(
          actions.dataObjectForMutation.inputObjectName,
          mutationData,
          actions.dataObjectForMutation.inputSubObjectName,
          base_url,
        );
        if (Object.values(response)[0] != null) {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Line Removed successfully.`,
          });
          setRefreshTable(true);
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleVerifyGuarantor = async (row) => {
    try {
      const actionType =
        row.IsActive === "True" || row.IsActive === true || row.IsActive === 1
          ? "Unverify"
          : "Verify";
      const confirmation = await Swal.fire({
        title: `${actionType} Guarantor`,
        text: `Are you sure you want to ${actionType.toLowerCase()} this guarantor?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType.toLowerCase()}!`,
        cancelButtonText: "Not now, cancel",
      });
      if (confirmation.isConfirmed) {
        const parsedResults = await verifyGuarantor(row);
        if (parsedResults.results) {
          await Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Guarantor has been ${actionType.toLowerCase()}ed successfully.`,
          });
          setRefreshTable(true);
        } else {
          Swal.fire(
            "Error!",
            parsedResults.error || "Operation failed.",
            "error",
          );
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleApproveRejectLoanReversal = async (loanId, action) => {
    try {
      const params = { loanId, status: action };
      const actionType = action === 2 ? "Approve" : "Reject";
      const confirmation = await Swal.fire({
        title: `${actionType}`,
        text: `Are you sure you want to ${actionType} this  loan reversal?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType}!`,
        cancelButtonText: "Not now, cancel",
      });
      if (confirmation.isConfirmed) {
        const response = await executeAnyMutation(
          "approveRejectActiveLoanReversal",
          params,
          "param",
          "false",
          loanManagementUrl.uri,
        );
        const parsedResults = JSON.parse(response.results);
        if (parsedResults === "Loan has been reversed successfully.") {
          await Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Loan has been reversed successfully.`,
          });
          setRefreshTable(true);
        } else if (
          parsedResults === "Loan reversal request rejected successfully."
        ) {
          await Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Loan reversal request rejected successfully.`,
          });
          setRefreshTable(true);
        } else {
          Swal.fire("Error!", `${parsedResults}`, "error");
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleUpdateLoanLimit = async (
    customerId,
    companyId,
    initiatorId,
    newLoanLimit,
    action,
    customerName,
  ) => {
    try {
      const params = {
        customerId,
        flag: action,
        isBranch: actions.dataObject.parameters.isbranchapproval,
        isRegion: actions.dataObject.parameters.isregionapproval,
        isFieldOfficer: actions.dataObject.parameters.isfieldofficerapproval,
        isCompany: actions.dataObject.parameters.iscompanyapproval,
      };
      const flag =
        action === 1
          ? actions.approveLoanLimit
          : action === 0
            ? actions.rejectLoanLimitUpdate
            : actions.SetLoanLimit;
      const actionType = action === 1 ? "Approve" : "Reject";
      const confirmation = await Swal.fire({
        title: `${actionType}`,
        text: `Are you sure you want to ${actionType} ${customerName}'s loan limit?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType}!`,
        cancelButtonText: "Not now, cancel",
      });
      if (confirmation.isConfirmed) {
        const response = await executeAnyMutation(
          flag.inputObjectName,
          params,
          flag.modelName,
          "false",
          loanManagementUrl.uri,
        );
        const parsedResults = JSON.parse(response.results);
        if (parsedResults === "Loan Approved Successfully") {
          await Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Loan limit approved successfully`,
          });
          setRefreshTable(true);
        } else if (parsedResults === "Loan Limit Rejected Succesfully.") {
          await Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Loan limit rejected successfully`,
          });
          setRefreshTable(true);
        } else {
          throw new Error("Unexpected response structure.");
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleApproveLoanPenalty = async (loanid) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Approve Loan Penalty",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, approve it!`,
        cancelButtonText: "Cancel",
      });
      if (!confirmation.isConfirmed) return;
      const { value: penaltyapprovalcomments } = await Swal.fire({
        title: "Approval Comments",
        input: "text",
        inputPlaceholder: "Enter approval comments...",
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Cancel",
      });
      if (penaltyapprovalcomments === undefined) return;
      const params = {
        loanid,
        isgroup: actions.approvePenalty.isgroup,
        penaltyapprovalcomments,
        approval: 1,
      };
      const response = await executeAnyMutation(
        actions.approvePenalty.inputObjectName,
        params,
        actions.approvePenalty.modelName,
        "false",
        base_url,
      );
      if (response && Object.keys(response)[0]) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Penalty Approved Successfully",
        });
        setRefreshTable(true);
      } else {
        throw new Error("Unexpected Error Occured.");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request.Check your internet connection and try again.",
      });
    }
  };

  const handleRejectLoanPenalty = async (loanid) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Reject Penalty.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, reject it!`,
        cancelButtonText: "Cancel",
      });
      if (!confirmation.isConfirmed) return;
      const { value: penaltyapprovalcomments } = await Swal.fire({
        title: "Rejection Comments",
        input: "text",
        inputPlaceholder: "Enter approval comments...",
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Cancel",
      });
      if (penaltyapprovalcomments === undefined) return;
      const params = {
        loanid,
        isgroup: actions.approvePenalty.isgroup,
        penaltyapprovalcomments,
        approval: 0,
      };
      const response = await executeAnyMutation(
        actions.approvePenalty.inputObjectName,
        params,
        actions.approvePenalty.modelName,
        "false",
        base_url,
      );
      if (response && Object.keys(response)[0]) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Penalty Rejected Successfully.",
        });
        setRefreshTable(true);
      } else {
        throw new Error("Unexpected response structure.");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const formatFriendlyError = (rawErr) => {
    if (!rawErr)
      return "An unexpected error occurred while loading table data. Please try again.";
    let errStr =
      typeof rawErr === "object" ? JSON.stringify(rawErr) : String(rawErr);
    try {
      const parsed = JSON.parse(errStr);
      if (parsed?.Error) errStr = parsed.Error;
    } catch { }

    const lower = errStr.toLowerCase();
    if (
      lower.includes("postgres") ||
      lower.includes("npgsql") ||
      lower.includes("connection slots") ||
      lower.includes("53300") ||
      lower.includes("sqlstate") ||
      lower.includes("does not exist") ||
      lower.includes("syntax error") ||
      lower.includes("timeout") ||
      lower.includes("socket")
    ) {
      return "Unable to connect to the database or retrieve records due to a temporary server issue. Please click Retry or try again in a few moments.";
    }
    return errStr.length > 200
      ? "Failed to load records from server. Please try again later."
      : errStr;
  };

  const getData = useCallback(async () => {
    try {
      const response = await fetchJsonDynamic(
        actions.dataObject.objectName,
        actions.dataObject.inputObjectName,
        actions.dataObject.parameters,
        base_url,
      );

      if (!response || response?.error) {
        const friendlyMsg = formatFriendlyError(response?.error);
        setError(friendlyMsg);
        setIsLoading(false);
        setIsRefreshing(false);
      } else {
        const parsedResults = response?.results
          ? JSON.parse(response?.results)
          : [];
        const finalData = filterData
          ? parsedResults.filter(filterData)
          : parsedResults;
        setData(finalData);
        setUsedData(finalData);
        setFilteringdData(finalData);
        setDataArray(finalData);
        console.log("parsedResults", finalData);
        fetchColumns(finalData);
        setError(null);
        setIsLoading(false);
        setIsRefreshing(false);
      }
    } catch (err) {
      console.error("Error fetching table data:", err);
      setError(
        "Unable to connect to the database or retrieve records due to a temporary server issue. Please click Retry or try again in a few moments.",
      );
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [actions.dataObject.objectName, base_url, filterData]);

  function fetchColumns(keys = null) {
    if (columns === null || columns === undefined) {
      let columnsData = [];
      if (keys) {
        columnsData = generateColumnsFromResults(
          keys,
          actions.dataObject.ignoreFields,
        );
      } else if (typeof query === "string" && query.trim().length > 0) {
        columnsData = generateColumnsFromQuery(query, ignoreFields);
      }
      // If neither keys nor a valid query string is available, leave columns empty
      // (they will be generated once data loads via getData → fetchColumns(finalData))
      if (columnsData.length > 0) {
        setGeneratedColumn(columnsData);
      }
    } else {
      setGeneratedColumn(columns);
    }
  }

  useEffect(() => {
    fetchColumns();
  }, [columns]);

  useEffect(() => {
    let isMounted = true;
    getData();
    if (refreshTable && isMounted) {
      getData();
      setRefreshTable(false);
      onDataChanged?.();
    }
    return () => {
      isMounted = false;
    };
  }, [refreshTable]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "360px",
          p: 4,
          my: 4,
          mx: "auto",
          maxWidth: 580,
          borderRadius: "16px",
          bgcolor: isDark ? "rgba(239, 68, 68, 0.08)" : "#fef2f2",
          border: "1px dashed",
          borderColor: isDark ? "rgba(239, 68, 68, 0.3)" : "#fca5a5",
          textAlign: "center",
          boxShadow: isDark
            ? "0 8px 32px rgba(0,0,0,0.4)"
            : "0 8px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: isDark ? "rgba(239, 68, 68, 0.15)" : "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <ErrorOutlineIcon sx={{ color: "#ef4444", fontSize: 32 }} />
        </Box>
        <Typography
          variant="h6"
          fontWeight={700}
          color={isDark ? "#fca5a5" : "#991b1b"}
          gutterBottom
        >
          Unable to Load Data
        </Typography>
        <Typography
          variant="body2"
          color={isDark ? "rgba(255,255,255,0.72)" : "#7f1d1d"}
          sx={{ mb: 3, maxWidth: 460, lineHeight: 1.6 }}
        >
          {error}
        </Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<RefreshIcon />}
          onClick={() => {
            setError(null);
            setIsLoading(true);
            getData();
          }}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 3.5,
            py: 1,
            boxShadow: "0 4px 14px rgba(239, 68, 68, 0.3)",
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Circles
          height="60"
          width="60"
          color={colors.greenAccent[500]}
          ariaLabel="loading"
        />
      </Box>
    );
  }

  const filterActionsByRole = (action) => {
    const actionConfig = actions[action];
    if (!actionConfig) return false;
    if (actionConfig.permission === "hasNoPermission") {
      const hasValidRole = actionConfig.roleGroupId.includes(RoleGroupId);
      const hasValidCompany = actionConfig.companyIds
        ? actionConfig.companyIds.includes(CompanyId)
        : true;
      return hasValidRole && hasValidCompany;
    } else if (actions.isSubRight || actions.isSubright) {
      return hasSubRightPermission(actions.entity, actionConfig.permission);
    } else {
      return hasPermission(actions.entity, actionConfig.permission);
    }
  };

  // ─── Theme constants ───────────────────────────────────────────────────────
  // Brand blue is the app primary (#1766A2)
  const BRAND_BLUE = "#1766A2";
  const BRAND_BLUE_LIGHT = "#2196F3";

  // Dark mode palette
  const DARK_CARD = "#162736"; // card surface
  const DARK_HEADER = "#122033"; // header / footer / column headers
  const DARK_ROW_A = "rgba(255,255,255,0.025)"; // odd row
  const DARK_ROW_B = "transparent"; // even row
  const DARK_HOVER = "rgba(23,102,162,0.18)";
  const DARK_SELECT = "rgba(23,102,162,0.22)";
  const DARK_BORDER = "rgba(255,255,255,0.05)";
  const DARK_TOOLBAR = "rgba(0,0,0,0.3)";

  // Light mode palette
  const LIGHT_CARD = "#ffffff";
  const LIGHT_ROW_A = "#f4f7fb"; // odd row — cool off-white
  const LIGHT_ROW_B = "#ffffff"; // even row
  const LIGHT_HOVER = "#e8f0fe"; // periwinkle hover
  const LIGHT_SELECT = "rgba(23,102,162,0.07)";
  const LIGHT_BORDER = "#e4eaf3";
  const LIGHT_TOOLBAR = "#f0f4f9";
  const LIGHT_TEXT = "#1e293b"; // charcoal text in light rows
  const LIGHT_COLUMN_HEADER = "#1d3a5c"; // dark navy — between brand blue and old dark slate

  // ─── Chip render helpers ───────────────────────────────────────────────────

  const chipBase = {
    borderRadius: "5px",
    fontWeight: 700,
    fontSize: "0.69rem",
    height: 21,
    "& .MuiChip-label": { px: 1.1 },
  };

  const renderStatusChip = (isActive) => {
    const active = isActive === true || isActive === "True" || isActive === 1;
    return (
      <Chip
        size="small"
        label={active ? "Active" : "Inactive"}
        sx={{
          ...chipBase,
          bgcolor: active
            ? isDark
              ? "rgba(34,191,114,0.14)"
              : "rgba(34,191,114,0.1)"
            : isDark
              ? "rgba(210,40,54,0.14)"
              : "rgba(210,40,54,0.08)",
          color: active
            ? isDark
              ? "#4ecc8e"
              : "#1b995b"
            : isDark
              ? "#e47e86"
              : "#c0392b",
          border: `1px solid ${active
              ? isDark
                ? "rgba(78,204,142,0.35)"
                : "rgba(27,153,91,0.3)"
              : isDark
                ? "rgba(228,126,134,0.35)"
                : "rgba(192,57,43,0.25)"
            }`,
          animation: active ? `${chipPulse} 1.6s ease-in-out 2` : "none",
        }}
      />
    );
  };

  const renderBooleanChip = (value, trueLabel = "Yes", falseLabel = "No") => {
    const active = value === true || value === 1;
    return (
      <Chip
        size="small"
        label={active ? trueLabel : falseLabel}
        sx={{
          ...chipBase,
          height: 20,
          bgcolor: active
            ? isDark
              ? "rgba(34,191,114,0.12)"
              : "rgba(34,191,114,0.08)"
            : isDark
              ? "rgba(210,40,54,0.12)"
              : "rgba(210,40,54,0.07)",
          color: active
            ? isDark
              ? "#4ecc8e"
              : "#1b995b"
            : isDark
              ? "#e47e86"
              : "#c0392b",
        }}
      />
    );
  };

  const renderApprovalChip = (value) => {
    const configs = {
      0: {
        label: "Initial Stage",
        color: isDark ? "#e47e86" : "#c0392b",
        bg: isDark ? "rgba(210,40,54,0.12)" : "rgba(210,40,54,0.07)",
      },
      1: {
        label: "Pending",
        color: isDark ? "#fbbf24" : "#b45309",
        bg: isDark ? "rgba(251,191,36,0.12)" : "rgba(245,158,11,0.1)",
      },
      2: {
        label: "Approved",
        color: isDark ? "#4ecc8e" : "#1b995b",
        bg: isDark ? "rgba(34,191,114,0.12)" : "rgba(34,191,114,0.08)",
      },
    };
    const cfg = configs[value] || {
      label: "Unknown",
      color: isDark ? "#94a3b8" : "#64748b",
      bg: isDark ? "rgba(148,163,184,0.1)" : "rgba(100,116,139,0.07)",
    };
    return (
      <Chip
        size="small"
        label={cfg.label}
        sx={{ ...chipBase, height: 20, bgcolor: cfg.bg, color: cfg.color }}
      />
    );
  };

  const CellBox = ({ children }) => (
    <Box
      sx={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      {children}
    </Box>
  );

  // ─── Column definitions ────────────────────────────────────────────────────

  const dateFields = [
    "initiateddate",
    "approveddate",
    "dateapproved",
    "dateofbirth",
    "createddate",
    "createdat",
    "yearofestablishment",
  ];
  const dateTimeFields = [
    "fieldofficerapproveddate",
    "regionapproveddate",
    "branchverifieddate",
  ];
  const salesKeywords = ["sales", "revenue", "amount"];
  const columnsWithActions = [
    ...generatedColumn.map((column) => {
      const fieldLower = column.field.toLowerCase();
      column = {
        ...column,
        flex: Math.min(3, Math.max(0.7, (column.headerName?.length ?? 8) / 10)),
        minWidth: 120,
      };

      if (dateTimeFields.includes(fieldLower)) {
        return {
          ...column,
          renderCell: (params) => (
            <CellBox>
              {params.value
                ? format(new Date(params.value), "dd-MMM-yyyy HH:mm")
                : "—"}
            </CellBox>
          ),
        };
      }

      if (dateFields.includes(fieldLower)) {
        return {
          ...column,
          renderCell: (params) => (
            <CellBox>
              {params.value
                ? format(new Date(params.value), "dd-MMM-yyyy")
                : "—"}
            </CellBox>
          ),
        };
      }
      if (dateTimeFields.includes(fieldLower)) {
        return {
          ...column,
          renderCell: (params) => (
            <CellBox>
              {params.value
                ? format(new Date(params.value), "dd-MMM-yyyy HH:mm")
                : "—"}
            </CellBox>
          ),
        };
      }
      if (salesKeywords.some((kw) => fieldLower.includes(kw))) {
        return {
          ...column,
          renderCell: (params) => (
            <CellBox>
              {params.value != null
                ? params.value.toLocaleString("en-US")
                : "—"}
            </CellBox>
          ),
        };
      }

      if (column.field === "IsGroup")
        return {
          ...column,
          headerName: "Is Group",
          renderCell: (params) => {
            const value =
              params.value !== null && params.value !== undefined
                ? params.value
                : false;
            return (
              <Typography style={{ color: value ? "green" : "red" }}>
                {value ? "True" : "False"}
              </Typography>
            );
          },
        };
      if (column.field === "Acrues")
        return {
          ...column,
          headerName: "Acrues",
          renderCell: (params) => renderBooleanChip(params.value),
        };
      if (column.field === "CanCarryForward")
        return {
          ...column,
          headerName: "Can Carry Forward",
          renderCell: (params) => renderBooleanChip(params.value),
        };
      if (column.field === "IsDepositRequired")
        return {
          ...column,
          headerName: "Deposit Required",
          renderCell: (params) => renderBooleanChip(params.value),
        };
      if (column.field === "IsDepositPercentage")
        return {
          ...column,
          headerName: "Deposit Percentage",
          renderCell: (params) => renderBooleanChip(params.value),
        };

      if (column.field === "Active_Reversal_Status") {
        return {
          ...column,
          headerName: "Status",
          renderCell: (params) => {
            const statusMap = {
              1: { label: "Rejected", color: "red" },
              2: { label: "Approved", color: "green" },
              0: { label: "Pending Approval", color: "orange" },
            };
            const { label, color } = statusMap[params.value] ?? {
              label: "Unknown",
              color: "grey",
            };
            return <Typography style={{ color }}>{label}</Typography>;
          },
        };
      }

      if (column.field === "IsApproved") {
        return {
          ...column,
          renderCell: (params) => renderApprovalChip(params.value),
        };
      }

      if (column.field === "id") {
        return {
          ...column,
          renderCell: (params) => params.api.getRowIndex(params.row.Id) + 1,
        };
      }

      // Respect a renderCell the caller already supplied (e.g. status chips
      // built from a field other than `field`, like AssetStatus) instead of
      // silently discarding it in favor of the plain params.value fallback.
      if (column.renderCell) {
        return column;
      }

      return {
        ...column,
        renderCell: (params) => {
          if (typeof params.value === "boolean") {
            return <CellBox>{params.value ? "Yes" : "No"}</CellBox>;
          }
          if (
            typeof params.value === "number" &&
            /amount|balance|value|limit|fee|price|cost|total|sum/i.test(
              column.field,
            )
          ) {
            return (
              <CellBox>
                {params.value.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </CellBox>
            );
          }
          if (typeof params.value === "string" && params.value.includes("T")) {
            const parts = params.value.split("T");
            if (parts.length === 2 && /^\d{4}-\d{2}-\d{2}$/.test(parts[0])) {
              return <CellBox>{parts[0]}</CellBox>;
            }
          }
          return <CellBox>{params.value ?? "—"}</CellBox>;
        },
      };
    }),

    ...(actions.showStatus !== true
      ? [
        {
          field: "IsActive",
          headerName: "Status",
          flex: 0.8,
          minWidth: 90,
          renderCell: ({ row: { IsActive } }) => renderStatusChip(IsActive),
        },
      ]
      : []),

    {
      field: "actions",
      headerName: "Actions",
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title="Row actions" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, row)}
            data-tour="actions-dropdown"
            className="dynamic-table-actions"
            sx={{
              borderRadius: "8px",
              width: 30,
              height: 30,
              color: isDark ? "rgba(255,255,255,0.6)" : BRAND_BLUE,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(23,102,162,0.3)"}`,
              transition: "all 0.16s ease",
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(23,102,162,0.2)"
                  : "rgba(23,102,162,0.08)",
                color: isDark ? "#fff" : BRAND_BLUE,
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(23,102,162,0.55)",
                transform: "scale(1.07)",
              },
              "&:active": {
                transform: "scale(0.88)",
                transition: "transform 0.08s ease",
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // ─── Form & modal handlers ─────────────────────────────────────────────────

  const handleAdd = () => {
    setIsEditing(false);
    setEditData(null);
    if (isMobile) setDialogOpen(true);
    else setDrawerOpen(true);
  };

  const handleEdit = (Id) => {
    const itemToEdit = data.find((item) => item.Id === Id);
    setEditData(itemToEdit);
    setIsEditing(true);
    if (isMobile) setDialogOpen(true);
    else setDrawerOpen(true);
  };

  const handleMarkRegFeePaid = async (Id) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "This action will mark the registration fee as paid. Do you want to Proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, mark as paid!",
      });
      if (!confirmation.isConfirmed) return;
      const response = await executeAnyMutation(
        "markRegFeePaidManually",
        { customerid: Id },
        "regFeePaid",
        false,
        customerservice.uri,
      );
      if (response?.error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to mark the registration fee as paid.",
        });
        return;
      }
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Registration fee marked as paid successfully!",
      });
      setRefreshTable(true);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "An unexpected error occurred.",
      });
    }
  };

  const handleSubmitStaffClaim = async (Id) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "This action will Submit the staffClaim for Approval. Proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Submit!",
      });
      if (!confirmation.isConfirmed) return;
      const response = await executeAnyMutation(
        " submitStaffClaim",
        { id: Id },
        "staffClaim",
        null,
        HRService.uri,
      );
      if (response?.error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to Submit StaffClaim.",
        });
        return;
      }
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "StaffClaim Submitted successfully!",
      });
      setRefreshTable(true);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "An unexpected error occurred.",
      });
    }
  };

  const handleSubmitImprest = async (Id) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "This action will Submit the Imprest Surrender. Proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Submit!",
      });
      if (!confirmation.isConfirmed) return;
      const response = await executeAnyMutation(
        " submitImprest",
        { id: Id },
        "imprestSurrender",
        null,
        HRService.uri,
      );
      if (response?.error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to Submit Imprest Surrender.",
        });
        return;
      }
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Imprest Surrender Submitted successfully!",
      });
      setRefreshTable(true);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "An unexpected error occurred.",
      });
    }
  };

  const handleRowSelection = (Id) => {
    const selectedRowData = data.find((row) => row.Id === Id);
    setSelectedCustomer(selectedRowData);
  };

  const handleOpenModal = (row) => {
    setRowData(row);
    setOpenLoanModal(true);
  };
  const handleOpenAssetLoanModal = (row) => {
    setRowData(row);
    setOpenAssetLoanModal(true);
  };
  const handleCloseAssetLoanModal = () => setOpenAssetLoanModal(false);
  const handleOpenDeviceLoanModal = (row) => {
    setRowData(row);
    setOpenDeviceLoanModal(true);
  };
  const handleCloseDeviceLoanModal = () => setOpenDeviceLoanModal(false);
  const handleApproveRejectManualPayment = async (row, action) => {
    try {
      const isReject = action === 2;
      const confirmation = await Swal.fire({
        title: isReject ? "Reject" : "Approve",
        text: `Are you sure you want to ${isReject ? "reject" : "approve"} this manual payment of ${row.Amount} for ${row.CustomerName}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: isReject ? "Yes, reject!" : "Yes, approve!",
        cancelButtonText: "Not now, cancel",
        input: isReject ? "text" : undefined,
        inputPlaceholder: isReject ? "Reason for rejection" : undefined,
        inputValidator: isReject
          ? (value) =>
            !value ? "Please provide a reason for rejection" : undefined
          : undefined,
      });
      if (!confirmation.isConfirmed) return;

      const narration = isReject
        ? String(confirmation.value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
        : null;

      const manualPayment = {
        customerId: null,
        loanId: null,
        amount: null,
        manualPaymentId: row.ManualPaymentId,
        action,
        narration,
      };

      const response = await executeAnyMutation(
        "insertManualPayment",
        manualPayment,
        "manualPayment",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.results) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Manual payment ${isReject ? "rejected" : "approved"} successfully.`,
        });
        setRefreshTable(true);
      } else if (response?.error) {
        fireMutationError(response.error);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleApproveRejectInterestWaiver = async (row, action) => {
    try {
      const isReject = action === 2;
      const confirmation = await Swal.fire({
        title: isReject ? "Reject" : "Approve",
        text: `Are you sure you want to ${isReject ? "reject" : "approve"} this interest waiver of ${row.RequestedAmount} for ${row.CustomerName}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: isReject ? "Yes, reject!" : "Yes, approve!",
        cancelButtonText: "Not now, cancel",
        input: isReject ? "text" : undefined,
        inputPlaceholder: isReject ? "Reason for rejection" : undefined,
        inputValidator: isReject
          ? (value) =>
            !value ? "Please provide a reason for rejection" : undefined
          : undefined,
      });
      if (!confirmation.isConfirmed) return;

      const narration = isReject
        ? String(confirmation.value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
        : null;

      const interestWaiver = {
        loanId: null,
        amount: null,
        waiverId: row.Id,
        action,
        narration,
      };

      const response = await executeAnyMutation(
        "insertInterestWaiver",
        interestWaiver,
        "interestWaiver",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.results) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Interest waiver ${isReject ? "rejected" : "approved"} successfully.`,
        });
        setRefreshTable(true);
      } else if (response?.error) {
        fireMutationError(response.error);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleApproveRejectVoluntaryRepossession = async (row, action) => {
    try {
      const isReject = action === 2;
      const confirmation = await Swal.fire({
        title: isReject ? "Reject" : "Approve",
        text: `Are you sure you want to ${isReject ? "reject" : "approve"} this voluntary repossession request for ${row.CustomerName}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: isReject ? "Yes, reject!" : "Yes, approve!",
        cancelButtonText: "Not now, cancel",
        input: isReject ? "text" : undefined,
        inputPlaceholder: isReject ? "Reason for rejection" : undefined,
        inputValidator: isReject
          ? (value) =>
            !value ? "Please provide a reason for rejection" : undefined
          : undefined,
      });
      if (!confirmation.isConfirmed) return;

      const narration = isReject
        ? String(confirmation.value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
        : null;

      const voluntaryRepossession = {
        collateralAssetId: null,
        actionBy: UserId,
        companyId: null,
        requestId: row.Id,
        action,
        narration,
      };

      const response = await executeAnyMutation(
        "insertVoluntaryRepossession",
        voluntaryRepossession,
        "voluntaryRepossession",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.results) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Voluntary repossession request ${isReject ? "rejected" : "approved"} successfully.`,
        });
        setRefreshTable(true);
      } else if (response?.error) {
        fireMutationError(response.error);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleApproveRejectValuation = async (row, status) => {
    try {
      const actionType = status === "APPROVED" ? "Approve" : "Reject";
      const confirmation = await Swal.fire({
        title: actionType,
        text: `Are you sure you want to ${actionType.toLowerCase()} this valuation?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionType.toLowerCase()}!`,
        cancelButtonText: "Not now, cancel",
      });
      if (!confirmation.isConfirmed) return;

      const response = await executeAnyMutation(
        "updateAssetValuation",
        { id: row.Id, status },
        "assetValuation",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.results) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Valuation ${actionType.toLowerCase()}d successfully.`,
        });
        setRefreshTable(true);
      } else if (response?.error) {
        Swal.fire({ icon: "error", title: "Error!", text: response.error });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };
  const handleOpenGroupModal = (row) => {
    setRowData(row);
    setOpenGroupLoanModal(true);
  };
  const handleOpenSetLoanLimitModal = (row) => {
    setRowData(row);
    setOpenLoanLimitModel(true);
  };
  const handleOpenValuationModal = (row) => {
    setRowData(row);
    setOpenValuationModal(true);
  };
  const handleCloseValuationModal = () => {
    setOpenValuationModal(false);
  };
  const handleCloseLoanLimitModal = (reopen = false) => {
    setOpenLoanLimitModel(false);
    if (reopen) setTimeout(() => setOpenLoanLimitModel(true), 300);
  };
  const handleCloseLeaveModal = (reopen = false) => {
    setOpenLeaveModul(false);
    if (reopen) setTimeout(() => setOpenLeaveModul(true), 300);
  };
  const handleCloseClaimModal = (reopen = false) => {
    setOpenStaffClaim(false);
    if (reopen) setTimeout(() => setOpenStaffClaim(true), 300);
  };
  const handleCloseImprestModal = (reopen = false) => {
    setOpenImprestModal(false);
    if (reopen) setTimeout(() => setOpenImprestModal(true), 300);
  };
  const handleOpenSetMappingToEmployer = (row) => {
    setRowData(row);
    setOpenMappingToEmployer(true);
  };
  const handleOpenLeaveModul = (row) => {
    setRowData(row);
    setOpenLeaveModul(true);
  };
  const handleOpenStaffClaim = (row) => {
    setRowData(row);
    setOpenStaffClaim(true);
  };
  const handleOpenImprestModal = (row) => {
    setRowData(row);
    setOpenImprestModal(true);
  };
  const handleCloseMappingToEmployer = (reopen = false) => {
    setOpenMappingToEmployer(false);
    if (reopen) setTimeout(() => setOpenMappingToEmployer(true), 300);
  };
  const handleCloseModal = () => setOpenLoanModal(false);

  const handleRemoveCustomerFromGroup = async (customerId, groupId, row) => {
    if (row.IsActive) {
      await Swal.fire({
        icon: "warning",
        title: "Member is still active!",
        text: "Please deactivate the member first before removing them from the group.",
      });
      return;
    }
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "This action will remove the customer from the group. Proceed?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, remove it!",
      });
      if (!confirmation.isConfirmed) return;
      const response = await executeAnyMutation(
        "removeCustomerFromCustomerGroup",
        { customerId, groupId },
        "",
        false,
        customerservice.uri,
      );
      if (response?.error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to remove the customer from the group.",
        });
        return;
      }
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Customer removed from the group successfully!",
      });
      setRefreshTable(true);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "An unexpected error occurred.",
      });
    }
  };

  const handleTransferApproval = async (row, isAccepted) => {
    try {
      const isIdTransfer = !!row.Newidnumber;
      const mutationName = isIdTransfer
        ? "customerNationIdTransferApproval"
        : "customerPhoneNumberTransferApproval";
      const inputObjectName = isIdTransfer
        ? "id_Number_Transfer_Approval"
        : "phone_Number_Transfer_Approval";
      const params = { p_Customerid: row.Id, is_Accepted: isAccepted };
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: `${isAccepted ? "Approve" : "Reject"} ${isIdTransfer ? "ID" : "Phone Number"} Transfer?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${isAccepted ? "Approve" : "Reject"} ${isIdTransfer ? "ID" : "Phone Number"} Transfer!`,
        cancelButtonText: "Cancel",
      });
      if (confirmation.isConfirmed) {
        const response = await executeAnyMutation(
          mutationName,
          params,
          inputObjectName,
          false,
          base_url,
        );
        if (response && response.results === "200") {
          await Swal.fire({
            icon: "success",
            title: "Success!",
            text: `${isIdTransfer ? "ID" : "Phone Number"} Transfer ${isAccepted ? "Approved" : "Rejected"} Successfully.`,
          });
          setRefreshTable(true);
        } else {
          throw new Error(response.error || "Unexpected response structure.");
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
  };

  const handleAction = async (action, Id, row) => {
    switch (action) {
      case "AuditTrail":
        if (actions.AuditTrail?.onAction) {
          actions.AuditTrail.onAction(row);
        } else {
          setSelectedAuditRow(row);
          setAuditModalOpen(true);
        }
        break;
      case "ViewStatementReport":
        handleViewStatement(row);
        break;

      case "edit":
        handleEdit(Id);
        break;
      case "ViewRelievers":
        localStorage.setItem("MyLeaveApplicationsData", JSON.stringify(row));
        navigate("/relievers");
        break;
      case "ViewSurrenderLineByApplication":
        localStorage.setItem("SurrenderId", JSON.stringify(row));
        navigate("/imprest-Surrender-Lines");
        break;
      case "viewStaffClaimsLines":
        localStorage.setItem("claimApplicationId", JSON.stringify(row.Id));
        navigate("/staff-claim-Lines");
        break;
      case "removeLeaveApprover":
        await removeLeaveApprover(row);
        break;
      case "removeSurrenderLine":
        await removeSurrenderLine(row);
        break;
      case "add":
        handleAdd();
        break;
      case "ManageGroup":
        localStorage.setItem("GroupId", Id);
        navigate("/managegroup");
        break;
      case "ViewCustomerDetails":
        localStorage.setItem("CustomerId", Id);
        localStorage.setItem("Status", row.IsApproved);
        localStorage.setItem(
          "IsAssetFinanceCustomer",
          row.IsAssetFinanceCustomer,
        );
        localStorage.setItem(
          "IsDeviceCustomer",
          row.IsDeviceCustomer
        );
        {
          const cName = [row.FirstName, row.OtherNames || row.LastName].filter(Boolean).join(" ").trim() || row.CustomerName || row.FullName || row.Name || "";
          if (cName) {
            localStorage.setItem("CustomerName", cName);
            localStorage.setItem("CustomerName_Id", String(Id));
          }
        }
        navigate("/customer-details");
        break;
      case "ViewCustomerDetailsApproval":
        localStorage.setItem("CustomerId", Id);
        localStorage.setItem("RightIdFromSidebar", 26);
        localStorage.setItem("Status", row.IsApproved);
        localStorage.setItem(
          "IsAssetFinanceCustomer",
          row.IsAssetFinanceCustomer,
        );
        localStorage.setItem(
          "IsDeviceCustomer",
          row.IsDeviceCustomer
        );
        {
          const cName = [row.FirstName, row.OtherNames || row.LastName].filter(Boolean).join(" ").trim() || row.CustomerName || row.FullName || row.Name || "";
          if (cName) {
            localStorage.setItem("CustomerName", cName);
            localStorage.setItem("CustomerName_Id", String(Id));
          }
        }
        navigate("/customer-details");
        break;
      case "ManageCollateralAssets":
        localStorage.setItem("CustomerId", Id);
        localStorage.setItem("Status", row.IsApproved);
        localStorage.setItem(
          "IsAssetFinanceCustomer",
          row.IsAssetFinanceCustomer,
        );
        localStorage.setItem(
          "IsDeviceCustomer",
          row.IsDeviceCustomer
        );
        {
          const cName = [row.FirstName, row.OtherNames || row.LastName].filter(Boolean).join(" ").trim() || row.CustomerName || row.FullName || row.Name || "";
          if (cName) {
            localStorage.setItem("CustomerName", cName);
            localStorage.setItem("CustomerName_Id", String(Id));
          }
        }
        navigate("/customer-details");
        break;
      case "ViewCustomer360":
        localStorage.setItem("CustomerId", Id);
        navigate("/customer-360");
        break;
      case "ViewGuarantor360":
        localStorage.setItem("guarantorId", Id);
        navigate("/guarantor-360");
        break;
      case "ApproveDisapproveCustomer":
        localStorage.setItem("MobileNumber", row.MobileNumber);
        localStorage.setItem("CustomerId", row.Id);
        navigate("/customer-approve-disapprove");
        break;
      case "CustomerCharges":
        localStorage.setItem("CustomerId", Id);
        localStorage.setItem(
          "CustomerName",
          `${row.FirstName} ${row.OtherNames || ""}`,
        );
        navigate("/customer-charges");
        break;
      case "VerifyGuarantor":
        handleVerifyGuarantor(row);
        break;
      case "ApproveReject":
        handleOpenModal(row);
        break;
      case "AssetApproveReject":
        handleOpenAssetLoanModal(row);
        break;
      case "DeviceApproveReject":
        handleOpenDeviceLoanModal(row);
        break;
      case "ApproveValuation":
        handleApproveRejectValuation(row, "APPROVED");
        break;
      case "RejectValuation":
        handleApproveRejectValuation(row, "REJECTED");
        break;
      case "ApproveManualPayment":
        handleApproveRejectManualPayment(row, 1);
        break;
      case "RejectManualPayment":
        handleApproveRejectManualPayment(row, 2);
        break;
      case "ApproveInterestWaiver":
        handleApproveRejectInterestWaiver(row, 1);
        break;
      case "RejectInterestWaiver":
        handleApproveRejectInterestWaiver(row, 2);
        break;
      case "ApproveVoluntaryRepossession":
        handleApproveRejectVoluntaryRepossession(row, 1);
        break;
      case "RejectVoluntaryRepossession":
        handleApproveRejectVoluntaryRepossession(row, 2);
        break;
      case "ApproveGroupReject":
        handleOpenGroupModal(row);
        break;
      case "SetLoanLimit":
        handleOpenSetLoanLimitModal(row);
        break;
      case "AddValuation":
        handleOpenValuationModal(row);
        break;
      case "activateDeactivate":
        handleActivateDeactivate(row.IsActive, row.Id);
        break;
      case "AprrovedRejectValuation":
        handleApproveDisaaproveCollateralValuation(
          row.ValuationStatus,
          row.Id,
          row.AssetId,
        );
        break;

      case "initiateSababisha":
        handleSababisha(row.LoanId, row.CompanyId, 0);
        break;
      case "approveSababisha":
        handleSababisha(row.LoanId, row.CompanyId, 1);
        break;
      case "cancelExpenseEntries":
        cancelExpenseEntries(row);
        break;
      case "ApproveLoanReversal":
        handleApproveRejectLoanReversal(row.LoanId, 2);
        break;
      case "RejectLoanReversal":
        handleApproveRejectLoanReversal(row.LoanId, 1);
        break;
      case "rejectLoanLimitUpdate":
        handleUpdateLoanLimit(
          row.Customerid,
          row.Companyid,
          row.Initiatorid,
          row.Requestedloanlimit,
          0,
          row.Customername,
        );
        break;
      case "approveLoanLimit":
        handleUpdateLoanLimit(
          row.Customerid,
          row.Companyid,
          row.Initiatorid,
          row.Requestedloanlimit,
          1,
          row.Customername,
        );
        break;
      case "phoneNumberTransferApproval":
        await handleTransferApproval(row, 1);
        break;
      case "rejectPhoneNumberTransfer":
        await handleTransferApproval(row, 0);
        break;
      case "ProductChargesTable":
        localStorage.setItem("ProductId", Id);
        navigate("/productcharges");
        break;
      case "documentRequirement":
      case "DocumentRequirement":
        localStorage.setItem("AssetTypeId", Id);
        navigate("/document-requirement");
        break;
      case "ProductChargesAccountsTable":
        localStorage.setItem("ProductId", Id);
        navigate("/productchargesaccount");
        break;
      case "removeCustomerFromGroup":
        handleRemoveCustomerFromGroup(
          row.CustomerId,
          row.GroupId,
          row.IsActive,
        );
        break;
      case "approvePenalty":
        handleApproveLoanPenalty(row.Id);
        break;
      case "rejectPenalty":
        handleRejectLoanPenalty(row.Id);
        break;
      case "MarkRegFeePaid":
        handleMarkRegFeePaid(Id);
        break;
      case "removeReliever":
        removeReleiever(row);
        break;
      case "removeImprestApprover":
        removeImprestApprover(row);
        break;
      case "submitStaffClaim":
        handleSubmitStaffClaim(Id);
        break;
      case "submitImprestSurrender":
        handleSubmitImprest(Id);
        break;
      case "MapToEmployer":
        handleOpenSetMappingToEmployer(row);
        break;
      case "ApproveLeave":
        handleOpenLeaveModul(row);
        break;
      case "ApproveStaffClaim":
        handleOpenStaffClaim(row);
        break;
      case "ApproveImprest":
        handleOpenImprestModal(row);
        break;
      case "ManageDirectors":
        localStorage.setItem("collateralAssetId", Id);
        localStorage.setItem("ownershiptype", row.OwnershipType);
        navigate("/directors");
        break;
      case "ManageCoOwner":
        localStorage.setItem("collateralAssetId", Id);
        localStorage.setItem("ownershiptype", row.OwnershipType);
        navigate("/CoOwners");
        break;
      default:
        if (
          actions &&
          actions[action] &&
          typeof actions[action].onClick === "function"
        ) {
          actions[action].onClick(row, () => setRefreshTable(true));
        }
        break;
    }
  };

  const handleSearch = async (input) => {
    if (generatedColumn && Array.isArray(generatedColumn)) {
      if (input.trim() !== "") {
        const filteredData = usedData.filter((row) =>
          generatedColumn.some((column) => {
            const cellValue = row[column.field];
            return (
              typeof cellValue === "string" &&
              cellValue.toLowerCase().includes(input.toLowerCase())
            );
          }),
        );

        if (
          actions.showMore.search === true &&
          filteredData.length === 0 &&
          input.length >= actions.showMore.min &&
          input.length <= actions.showMore.max
        ) {
          setSearch(true);
          if (actions.showMore.isCustomer === true) {
            try {
              const fetchedData = await fetchJsonDynamic(
                actions.showMore.objectname,
                actions.showMore.inputsubobjectname,
                {
                  [actions.showMore.parameters]: input,
                  approval: actions.showMore.otherParams,
                  isAssetCustomer: actions.showMore.isAssetCustomer,
                },
                customerservice.uri,
              );
              const parsedResults = JSON.parse(fetchedData.results);
              setData(
                parsedResults !== null
                  ? Array.isArray(parsedResults)
                    ? parsedResults
                    : [parsedResults]
                  : [],
              );
            } catch (error) {
              console.error("Error fetching data:", error);
            }
          } else {
            try {
              if (actions.showMore.searchType === "int")
                input = parseInt(input);
              const fetchedData = await fetchJsonDynamic(
                actions.showMore.objectname,
                actions.showMore.inputsubobjectname,
                { [actions.showMore.parameters]: input },
                actions.showMore.baseUrl,
              );
              setData(JSON.parse(fetchedData.results));
            } catch (error) {
              console.error("Error fetching data:", error);
            }
          }
        } else {
          setData(filteredData);
        }
      } else {
        setRefreshTable(true);
      }
    } else {
      console.error(
        "Columns or generatedColumns are not available for search.",
      );
    }
  };

  console.log("Sample Row:", data[0]);
  // Keep the ref pointing at the latest handleSearch (it closes over
  // usedData/generatedColumn/etc and is recreated every render) — the
  // debounced wrapper itself is created once, before the early returns above.
  handleSearchRef.current = handleSearch;

  // ─── Derived menu items for active row ────────────────────────────────────

  const visibleActions = activeMenuRow
    ? Object.keys(actions).filter(
      (action) =>
        actions[action] &&
        action !== "add" &&
        (typeof actions[action].Show_Button === "function"
          ? actions[action].Show_Button(activeMenuRow)
          : actions[action].Show_Button) &&
        filterActionsByRole(action),
    )
    : [];

  const destructiveActionKeys = new Set([
    "activateDeactivate",
    "removeLeaveApprover",
    "removeImprestApprover",
    "removeReliever",
    "removeSurrenderLine",
    "removeCustomerFromGroup",
    "cancelExpenseEntries",
    "rejectPhoneNumberTransfer",
    "rejectLoanLimitUpdate",
    "RejectLoanReversal",
    "rejectPenalty",
    "RejectValuation",
    "RejectManualPayment",
    "RejectInterestWaiver",
    "RejectVoluntaryRepossession",
  ]);

  // ─── Loading overlay with skeleton rows ───────────────────────────────────
  const SkeletonOverlay = () => (
    <Box sx={{ px: 2, pt: 1 }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={38}
          sx={{
            mb: 0.75,
            borderRadius: "6px",
            bgcolor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(23,102,162,0.07)",
            animationDelay: `${i * 80}ms`,
          }}
        />
      ))}
    </Box>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Joyride Tour */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: { primaryColor: colors.blueAccent[500], zIndex: 1000 },
        }}
      />

      {/* Welcome tour dialog */}
      <Dialog
        open={showModal}
        onClose={cancelTour}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            maxWidth: 400,
            bgcolor: isDark ? DARK_CARD : "#fff",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(23,102,162,0.15)"}`,
            boxShadow: isDark
              ? "0 24px 64px rgba(0,0,0,0.55)"
              : "0 16px 48px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 4 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "10px",
                bgcolor: isDark
                  ? "rgba(23,102,162,0.2)"
                  : "rgba(23,102,162,0.1)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <TableChartIcon
                sx={{
                  color: isDark ? BRAND_BLUE_LIGHT : BRAND_BLUE,
                  fontSize: 22,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight="700"
                sx={{ lineHeight: 1.2, color: isDark ? "#e2e8f0" : LIGHT_TEXT }}
              >
                Welcome to {title}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDark ? "rgba(255,255,255,0.45)" : "#64748b" }}
              >
                Guided tour available
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={cancelTour}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: isDark ? "rgba(255,255,255,0.4)" : "#94a3b8",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider
          sx={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "#e4eaf3" }}
        />
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.75,
              color: isDark ? "rgba(255,255,255,0.55)" : "#475569",
            }}
          >
            This page allows you to{" "}
            {subtitle?.toLowerCase?.() || "manage records"}. Would you like a
            quick guided tour to get started?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={cancelTour}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "0.8rem",
              borderColor: isDark
                ? "rgba(255,255,255,0.15)"
                : "rgba(23,102,162,0.3)",
              color: isDark ? "rgba(255,255,255,0.6)" : "#475569",
              "&:hover": {
                borderColor: isDark ? "rgba(255,255,255,0.3)" : BRAND_BLUE,
              },
            }}
          >
            Skip tour
          </Button>
          <Button
            onClick={startTour}
            size="small"
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8rem",
              background: isDark
                ? `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1e88e5 100%)`
                : `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`,
              boxShadow: `0 4px 14px rgba(23,102,162,0.4)`,
              "&:hover": {
                boxShadow: "0 6px 20px rgba(23,102,162,0.5)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.18s ease",
            }}
          >
            Take the tour
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main table card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 10px)",
          minHeight: 420,
          ...(isDark
            ? {
              bgcolor: DARK_CARD,
              border: "1px solid rgba(255,255,255,0.06)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
            }
            : {
              bgcolor: LIGHT_CARD,
              border: "1px solid #dde5ef",
              boxShadow:
                "0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(23,102,162,0.06)",
            }),
        }}
      >
        {/* Header strip */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            ...(isDark
              ? {
                background: `linear-gradient(135deg, ${DARK_HEADER} 0%, #1a3a54 100%)`,
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }
              : {
                background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`,
                borderBottom: `1px solid rgba(23,102,162,0.2)`,
              }),
          }}
          className="dynamic-table-header"
        >
          {/* Title + record badge */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h6"
                fontWeight="700"
                sx={{
                  color: "#fff",
                  fontSize: { xs: "0.92rem", sm: "1rem" },
                  letterSpacing: "0.3px",
                  textTransform: "uppercase",
                }}
              >
                {title}
              </Typography>
              <Chip
                key={data.length}
                label={data.length}
                size="small"
                sx={{
                  bgcolor: alpha("#fff", 0.18),
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.68rem",
                  height: 19,
                  borderRadius: "5px",
                  "& .MuiChip-label": { px: 0.9 },
                  animation: `${popIn} 0.35s cubic-bezier(0.34,1.56,0.64,1)`,
                }}
              />
            </Box>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: alpha("#fff", 0.72), fontWeight: 500 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Search + Add */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            {title && (
              <TextField
                variant="outlined"
                size="small"
                value={searchTerm || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Update the visible text synchronously so typing is never
                  // clobbered by an in-flight async search resolving late —
                  // only the actual search/filter side effect is debounced.
                  setSearchTerm(value);
                  debouncedSearch(value);
                }}
                placeholder={`Search ${title}...`}
                className="dynamic-table-search"
                sx={{
                  width: { xs: "100%", sm: 210, md: 250 },
                  transition: "width 0.25s ease",
                  "&:focus-within": { width: { xs: "100%", sm: 290, md: 340 } },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: alpha("#fff", 0.1),
                    backdropFilter: "blur(6px)",
                    fontSize: "0.81rem",
                    color: "#fff",
                    height: 36,
                    "& fieldset": { borderColor: alpha("#fff", 0.28) },
                    "&:hover fieldset": { borderColor: alpha("#fff", 0.55) },
                    "&.Mui-focused fieldset": {
                      borderColor: alpha("#fff", 0.8),
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: alpha("#fff", 0.55),
                      opacity: 1,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 16, color: alpha("#fff", 0.7) }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          debouncedSearch.cancel();
                          setSearchTerm("");
                          setRefreshTable(true);
                        }}
                        sx={{
                          color: alpha("#fff", 0.65),
                          p: 0.25,
                          "&:hover": { color: "#fff" },
                        }}
                      >
                        <ClearIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}

            <Tooltip title="Refresh" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  setRefreshTable(true);
                  setIsRefreshing(true);
                  setIsLoading(true);
                }}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  color: alpha("#fff", 0.85),
                  border: `1px solid ${alpha("#fff", 0.28)}`,
                  "&:hover": {
                    backgroundColor: alpha("#fff", 0.15),
                    borderColor: alpha("#fff", 0.55),
                    color: "#fff",
                  },
                  transition: "all 0.18s ease",
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 18,
                    animation: isRefreshing
                      ? `${spinAnim} 0.7s linear infinite`
                      : "none",
                  }}
                />
              </IconButton>
            </Tooltip>

            {actions.add &&
              actions.add.Show_Button &&
              (actions.isSubRight || actions.isSubright
                ? hasSubRightPermission(actions.entity, actions.add.permission)
                : hasPermission(actions.entity, actions.add.permission)) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: "17px !important" }} />}
                  onClick={() => handleAction("add")}
                  className="dynamic-table-add-button"
                  size="small"
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.81rem",
                    px: 2,
                    py: 0.75,
                    whiteSpace: "nowrap",
                    height: 36,
                    color: "#fff",
                    background: isDark
                      ? "linear-gradient(135deg, #1b995b 0%, #22bf72 100%)"
                      : "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
                    boxShadow: isDark
                      ? "0 4px 14px rgba(34,191,114,0.35)"
                      : "0 4px 14px rgba(22,163,74,0.35)",
                    "&:hover": {
                      background: isDark
                        ? "linear-gradient(135deg, #147344 0%, #1b995b 100%)"
                        : "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
                      boxShadow: isDark
                        ? "0 6px 20px rgba(34,191,114,0.45)"
                        : "0 6px 20px rgba(22,163,74,0.45)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.18s ease",
                  }}
                >
                  {actions.add.button_name}
                </Button>
              )}
          </Box>
        </Box>

        {/* DataGrid */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <DataGrid
            checkboxSelection
            rows={data}
            columns={columnsWithActions}
            loading={isLoading}
            components={{
              Toolbar: GridToolbar,
              LoadingOverlay: SkeletonOverlay,
            }}
            onRowSelectionModelChange={(Id) => handleRowSelection(Id)}
            // Column virtualization only mounts cells near the current
            // horizontal scroll position, which unmounts the sticky
            // "actions" column (making it disappear) until you scroll all
            // the way right. Keep every column mounted so the sticky CSS
            // below can actually pin it in place.
            columnBuffer={columnsWithActions.length}
            className="dynamic-table-grid"
            getRowId={(row) => row.Id ?? row.LoanChargesId ?? Math.random()}
            sx={{
              border: "none",
              fontSize: "0.8rem",
              bgcolor: isDark ? DARK_CARD : LIGHT_CARD,

              // ── Column headers ─────────────────────────────────────────────
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: isDark ? DARK_HEADER : LIGHT_COLUMN_HEADER,
                color: "#fff",
                borderBottom: "none",
                fontSize: "0.71rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                minHeight: "42px !important",
                maxHeight: "42px !important",
                lineHeight: "42px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
              "& .MuiDataGrid-sortIcon": { color: "rgba(255,255,255,0.75)" },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgba(255,255,255,0.7)",
              },
              "& .MuiDataGrid-columnSeparator": {
                color: isDark
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(255,255,255,0.2)",
              },

              // ── Cells ──────────────────────────────────────────────────────
              "& .MuiDataGrid-cell": {
                borderColor: isDark ? DARK_BORDER : LIGHT_BORDER,
                alignItems: "center",
                color: isDark ? "rgba(255,255,255,0.82)" : LIGHT_TEXT,
                "&:focus, &:focus-within": { outline: "none" },
              },

              // ── Alternating rows ───────────────────────────────────────────
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: isDark ? DARK_ROW_A : LIGHT_ROW_A,
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: isDark ? DARK_ROW_B : LIGHT_ROW_B,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: isDark ? DARK_HOVER : LIGHT_HOVER,
                transition: "background-color 0.14s ease",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: isDark ? DARK_SELECT : LIGHT_SELECT,
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(23,102,162,0.28)"
                    : "rgba(23,102,162,0.1)",
                },
              },

              // ── Footer ─────────────────────────────────────────────────────
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(23,102,162,0.15)"}`,
                backgroundColor: isDark ? DARK_HEADER : LIGHT_COLUMN_HEADER,
                color: "#fff",
                minHeight: 42,
              },
              "& .MuiTablePagination-root": { color: "rgba(255,255,255,0.85)" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                color: "rgba(255,255,255,0.78)",
              },
              "& .MuiTablePagination-select, & .MuiTablePagination-selectIcon":
              {
                color: "rgba(255,255,255,0.85)",
              },
              "& .MuiDataGrid-selectedRowCount": {
                color: "rgba(255,255,255,0.75)",
              },
              "& .MuiDataGrid-footerContainer .MuiIconButton-root": {
                color: "rgba(255,255,255,0.8)",
              },
              "& .MuiDataGrid-footerContainer .MuiIconButton-root.Mui-disabled":
              {
                color: "rgba(255,255,255,0.3)",
              },

              // ── Toolbar ────────────────────────────────────────────────────
              "& .MuiDataGrid-toolbarContainer": {
                borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : LIGHT_BORDER}`,
                px: 1.5,
                py: 0.5,
                backgroundColor: isDark ? DARK_TOOLBAR : LIGHT_TOOLBAR,
              },
              "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${isDark ? "rgba(255,255,255,0.65)" : "#475569"} !important`,
                fontSize: "0.74rem",
                textTransform: "none",
                fontWeight: 500,
              },
              "& .MuiDataGrid-toolbarContainer .MuiSvgIcon-root": {
                color: isDark ? "rgba(255,255,255,0.5)" : "#64748b",
                fontSize: "1rem",
              },

              // ── Checkboxes ─────────────────────────────────────────────────
              "& .MuiCheckbox-root": {
                color: isDark
                  ? "rgba(255,255,255,0.25) !important"
                  : "rgba(23,102,162,0.35) !important",
              },
              "& .MuiCheckbox-root.Mui-checked": {
                color: isDark
                  ? "#4ecc8e !important"
                  : `${BRAND_BLUE} !important`,
              },

              // ── Virtual scroller ───────────────────────────────────────────
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: "transparent",
              },

              // ── Row border + fade-in stagger (desktop only via media query) ──
              "& .MuiDataGrid-row": {
                borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : LIGHT_BORDER}`,
                "@media (min-width:600px)": {
                  animation: `${fadeInUp} 0.32s ease both`,
                },
              },
              "& .MuiDataGrid-row:nth-of-type(2)": {
                "@media (min-width:600px)": { animationDelay: "30ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(3)": {
                "@media (min-width:600px)": { animationDelay: "60ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(4)": {
                "@media (min-width:600px)": { animationDelay: "90ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(5)": {
                "@media (min-width:600px)": { animationDelay: "120ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(6)": {
                "@media (min-width:600px)": { animationDelay: "150ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(7)": {
                "@media (min-width:600px)": { animationDelay: "180ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(8)": {
                "@media (min-width:600px)": { animationDelay: "210ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(9)": {
                "@media (min-width:600px)": { animationDelay: "240ms" },
              },
              "& .MuiDataGrid-row:nth-of-type(10)": {
                "@media (min-width:600px)": { animationDelay: "270ms" },
              },

              // ── Sticky actions cell ────────────────────────────────────────
              "& .MuiDataGrid-cell[data-field='actions']": {
                position: "sticky",
                right: 0,
                zIndex: 2,
                backgroundColor: isDark ? DARK_CARD : LIGHT_CARD,
                boxShadow: isDark
                  ? "-3px 0 8px rgba(0,0,0,0.4)"
                  : "-3px 0 8px rgba(0,0,0,0.07)",
              },
              "& .MuiDataGrid-row:nth-of-type(odd) .MuiDataGrid-cell[data-field='actions']":
              {
                backgroundColor: isDark ? DARK_ROW_A : LIGHT_ROW_A,
              },
              "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field='actions']":
              {
                backgroundColor: isDark ? DARK_ROW_B : LIGHT_ROW_B,
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='actions']":
              {
                backgroundColor: isDark ? DARK_HOVER : LIGHT_HOVER,
              },
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field='actions']":
              {
                backgroundColor: isDark ? DARK_SELECT : LIGHT_SELECT,
              },
            }}
          />
        </Box>

        {selectedCustomer && (
          <CustomerStatementDetails customer={selectedCustomer} />
        )}
      </Paper>

      {/* Row action menu (shared, single instance) */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "12px",
            minWidth: 210,
            mt: 0.5,
            overflow: "visible",
            ...(isDark
              ? {
                bgcolor: "#1a3448",
                border: "1px solid rgba(255,255,255,0.09)",
                boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
              }
              : {
                bgcolor: "#fff",
                border: "1px solid #dde5ef",
                boxShadow:
                  "0 4px 24px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.06)",
              }),
            // Dropdown arrow caret
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 9,
              height: 9,
              bgcolor: isDark ? "#1a3448" : "#fff",
              transform: "translateY(-50%) rotate(45deg)",
              borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "#dde5ef"}`,
              borderLeft: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "#dde5ef"}`,
            },
            "& .MuiMenuItem-root": {
              fontSize: "0.82rem",
              fontWeight: 500,
              py: 0.9,
              px: 2,
              mx: 0.5,
              borderRadius: "7px",
              transition: "all 0.12s ease",
              color: isDark ? "rgba(255,255,255,0.82)" : LIGHT_TEXT,
            },
            // Padding inside the list
            "& .MuiList-root": { py: 0.75 },
          },
        }}
      >
        {visibleActions.map((action, index) => {
          const isDestructive = destructiveActionKeys.has(action);
          const isActivate = action === "activateDeactivate";
          const isCurrentlyActive =
            activeMenuRow?.IsActive === true ||
            activeMenuRow?.IsActive === 1 ||
            activeMenuRow?.IsActive === "True";

          const showAsDestructive =
            isDestructive && (!isActivate || isCurrentlyActive);

          const label = isActivate
            ? isCurrentlyActive
              ? "Deactivate"
              : "Activate"
            : actions[action].button_name;

          const prevAction = visibleActions[index - 1];
          const showDivider =
            index > 0 &&
            isDestructive &&
            prevAction &&
            !destructiveActionKeys.has(prevAction);

          return (
            <React.Fragment key={action}>
              {showDivider && (
                <Divider
                  sx={{
                    my: 0.5,
                    mx: 1,
                    borderColor: isDark ? "rgba(255,255,255,0.07)" : "#eef2f8",
                  }}
                />
              )}
              <MenuItem
                onClick={() => {
                  handleAction(action, activeMenuRow?.Id, activeMenuRow);
                  handleMenuClose();
                }}
                data-tour={`action-${action}`}
                sx={{
                  color: showAsDestructive
                    ? isDark
                      ? "#f87171"
                      : "#dc2626"
                    : isDark
                      ? "rgba(255,255,255,0.82)"
                      : LIGHT_TEXT,
                  "&:hover": {
                    backgroundColor: showAsDestructive
                      ? isDark
                        ? "rgba(220,38,38,0.12)"
                        : "rgba(220,38,38,0.06)"
                      : isDark
                        ? "rgba(23,102,162,0.2)"
                        : "rgba(23,102,162,0.07)",
                    color: showAsDestructive
                      ? isDark
                        ? "#fca5a5"
                        : "#b91c1c"
                      : isDark
                        ? "#fff"
                        : BRAND_BLUE,
                  },
                }}
              >
                {label}
              </MenuItem>
            </React.Fragment>
          );
        })}

        {visibleActions.length === 0 && (
          <MenuItem
            disabled
            sx={{
              fontSize: "0.82rem",
              color: isDark ? "rgba(255,255,255,0.3)" : "#94a3b8",
            }}
          >
            No actions available
          </MenuItem>
        )}
      </Menu>

      {/* Modals */}
      {openLoanModal && (
        <LoanApprovalDialog
          open={openLoanModal}
          onClose={handleCloseModal}
          Row={tableRowData}
          refetchData={() => setRefreshTable(true)}
          approvalParameters={actions.dataObject.parameters}
        />
      )}
      {openAssetLoanModal && (
        <AssetLoanApprovalDialog
          open={openAssetLoanModal}
          onClose={handleCloseAssetLoanModal}
          Row={tableRowData}
          refetchData={() => setRefreshTable(true)}
          approvalParameters={actions.dataObject.parameters}
        />
      )}
      {openDeviceLoanModal && (
        <DeviceLoanApprovalDialog
          open={openDeviceLoanModal}
          onClose={handleCloseDeviceLoanModal}
          Row={tableRowData}
          refetchData={() => setRefreshTable(true)}
          approvalParameters={actions.dataObject.parameters}
        />
      )}
      {openGroupLoanModal && (
        <GroupLoanApprovalDialog
          open={openGroupLoanModal}
          onClose={() => setOpenGroupLoanModal(false)}
          refetchData={() => setRefreshTable(true)}
          approvalParameters={actions.dataObject.parameters}
          Row={tableRowData}
        />
      )}
      {openLoanLimitModel && (
        <LoanLimitDialog
          open={openLoanLimitModel}
          onClose={handleCloseLoanLimitModal}
          Row={tableRowData}
        />
      )}
      {openValuationModal && (
        <CollateralValuationDialog
          open={openValuationModal}
          onClose={handleCloseValuationModal}
          Row={tableRowData}
          onSuccess={() => setRefreshTable(true)}
        />
      )}
      {openMappingToEmployer && (
        <CustomerEmployerMappingDialog
          open={openMappingToEmployer}
          onClose={handleCloseMappingToEmployer}
          Row={tableRowData}
        />
      )}
      {openLeaveModul && (
        <LeaveApplicationApprovalDialog
          open={openLeaveModul}
          onClose={handleCloseLeaveModal}
          Row={tableRowData}
        />
      )}
      {openStaffClaim && (
        <StaffClaimApprovalDialog
          open={openStaffClaim}
          onClose={handleCloseClaimModal}
          Row={tableRowData}
        />
      )}
      {openImprestModal && (
        <ImprestApprovalDialog
          open={openImprestModal}
          onClose={handleCloseImprestModal}
          Row={tableRowData}
        />
      )}

      {/* Drawer (desktop form) */}
      <AnchorTemporaryDrawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        FormComponent={() => (
          <FormComponent
            isEditing={isEditing}
            data={editData}
            onAction={() => setRefreshTable(true)}
            onClose={() => setDrawerOpen(false)}
            refetchData={() => setRefreshTable(true)}
          />
        )}
      />

      {/* Dialog (mobile form) */}
      {isMobile && (
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullScreen={isMobile}
          PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.5,
              px: 2,
              background: isDark
                ? `linear-gradient(135deg, ${DARK_HEADER} 0%, #1a3a54 100%)`
                : `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`,
              color: "#fff",
            }}
          >
            <Typography variant="h6" fontWeight="600" sx={{ fontSize: "1rem" }}>
              {isEditing ? `Edit ${title}` : `Add ${title}`}
            </Typography>
            <IconButton
              edge="end"
              onClick={() => setDialogOpen(false)}
              size="small"
              sx={{ color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2 }}>
            <FormComponent
              onClick={() => setDialogOpen(false)}
              isEditing={isEditing}
              data={editData}
              onAction={() => setRefreshTable(true)}
              onClose={() => setDialogOpen(false)}
              refetchData={() => setRefreshTable(true)}
            />
          </DialogContent>
        </Dialog>
      )}



      {selectedAuditRow && (
        <AuditTrailDialog
          open={auditModalOpen}
          onClose={() => {
            setAuditModalOpen(false);
            setSelectedAuditRow(null);
          }}
          title={`${title} Audit Trail`}
          entityType={title ? title.replace(/s$/, "") : "Record"}
          data={selectedAuditRow}
        />
      )}
    </Box>
  );
};

export default DynamicTableForJson;
